import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Col,
  Collapse,
  Drawer,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AppstoreAddOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  SearchOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { getActiveBrandId } from '../config/api';
import {
  EaseRanges,
  MeasurementInputMode,
  RecommendationScope,
  SizeChart,
  SizeChartCategoryType,
  SizeChartFitProfile,
  SizeChartPayload,
  SizeChartProduct,
  SizeChartService,
  StretchAllowances,
  isSizeChartConflict,
} from '../services/sizeChartService';
import './SizeCharts.css';

const { Title, Text, Paragraph } = Typography;

const LETTER_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
const NUMERIC_SIZES = Array.from({ length: 21 }, (_, index) => String(24 + index * 2));
const SIZE_OPTIONS = [...LETTER_SIZES, ...NUMERIC_SIZES].map(value => ({ value }));

const FIT_LABELS: Record<SizeChartFitProfile, string> = {
  compression: 'Kompresyon',
  fitted: 'Vücuda oturan',
  regular: 'Standart',
  relaxed: 'Rahat',
  oversized: 'Oversize',
};

const FIT_EASE_DEFAULTS: Record<SizeChartFitProfile, EaseRanges> = {
  compression: { chest: { minCm: -8, maxCm: -2 }, waist: { minCm: -8, maxCm: -2 }, hip: { minCm: -8, maxCm: -2 } },
  fitted: { chest: { minCm: 0, maxCm: 4 }, waist: { minCm: -1, maxCm: 3 }, hip: { minCm: 0, maxCm: 4 } },
  regular: { chest: { minCm: 4, maxCm: 8 }, waist: { minCm: 2, maxCm: 6 }, hip: { minCm: 2, maxCm: 6 } },
  relaxed: { chest: { minCm: 8, maxCm: 16 }, waist: { minCm: 6, maxCm: 14 }, hip: { minCm: 6, maxCm: 14 } },
  oversized: { chest: { minCm: 14, maxCm: 28 }, waist: { minCm: 12, maxCm: 24 }, hip: { minCm: 12, maxCm: 24 } },
};

const CATEGORY_LABELS = {
  upper: 'Üst giyim',
  lower: 'Alt giyim',
  full: 'Elbise / tulum / takım',
};

const BODY_LABELS = {
  female: 'Kadın',
  male: 'Erkek',
  unisex: 'Unisex',
};

const SIZE_SYSTEM_LABELS = {
  alpha: 'Harf beden (S / M / L)',
  eu_numeric: 'EU sayısal (32 / 34 / 36)',
  mixed: 'Karma',
  custom: 'Markaya özel',
};

const SIZE_GROUP_LABELS = {
  regular: 'Regular',
  petite: 'Petite',
  tall: 'Tall',
  plus: 'Plus',
  maternity: 'Maternity',
  custom: 'Özel grup',
};

const RECOVERY_LABELS = {
  unknown: 'Doğrulanmadı',
  low: 'Düşük',
  medium: 'Orta',
  high: 'Yüksek',
};

const STRETCH_EVIDENCE_LABELS = {
  unverified: 'Kanıt eklenmedi',
  supplier_spec: 'Tedarikçi teknik föyü',
  brand_tested: 'Marka tarafından test edildi',
  lab_tested: 'Laboratuvar testi',
};

const STRETCH_ZONE_LABELS = {
  chest: 'Göğüs',
  waist: 'Bel',
  hip: 'Basen',
};

const RECOMMENDATION_SCOPE_LABELS: Record<RecommendationScope, string> = {
  reference_only: 'Yalnız referans',
  girth_only_verified: 'Doğrulanmış çevre önerisi',
};

const PRODUCT_LENGTH_LABELS = {
  back_length: 'Arka orta boy',
  shoulder_to_hem: 'Omuzdan etek ucuna',
  side_length: 'Yan boy',
  outseam: 'Dış bacak / yan boy',
  skirt_length: 'Etek boyu',
  dress_length: 'Elbise boyu',
  other: 'Markaya özel POM',
};

const LEG_LENGTH_LABELS = {
  inseam: 'İç bacak (inseam)',
  outseam: 'Dış bacak (outseam)',
};

const emptyStretchAllowances = (): StretchAllowances => ({
  chest: { observedPercent: 0, recovery: 'unknown', evidence: 'unverified' },
  waist: { observedPercent: 0, recovery: 'unknown', evidence: 'unverified' },
  hip: { observedPercent: 0, recovery: 'unknown', evidence: 'unverified' },
});

const hasValue = (value: unknown): boolean => value !== null && value !== undefined && value !== '';

const assertRowsContract = (
  rows: any[],
  measurementMode: MeasurementInputMode,
  categoryType: SizeChartCategoryType,
  recommendationScope: RecommendationScope,
): void => {
  if (!Array.isArray(rows) || rows.length < 2 || rows.length > 80) {
    throw new Error('Bir tabloda 2 ile 80 arasında beden satırı olmalıdır.');
  }

  const labels = new Set<string>();
  const circumferenceMultiplier = measurementMode === 'flat_width' ? 2 : 1;
  rows.forEach((row, index) => {
    const label = String(row?.label || '').trim();
    if (label.length < 1 || label.length > 24) {
      throw new Error(`${index + 1}. satırda 1-24 karakterlik bir beden etiketi gereklidir.`);
    }
    const normalizedLabel = label.toLocaleLowerCase('tr-TR');
    if (labels.has(normalizedLabel)) {
      throw new Error('Beden etiketleri boşluklar temizlendikten sonra benzersiz olmalıdır.');
    }
    labels.add(normalizedLabel);

    const circumferenceFields = [
      ['chestCircumferenceCm', 'göğüs'],
      ['waistCircumferenceCm', 'bel'],
      ['hipCircumferenceCm', 'basen'],
    ] as const;
    circumferenceFields.forEach(([field, fieldLabel]) => {
      const value = row?.[field];
      if (!hasValue(value)) return;
      const canonicalValue = Number(value) * circumferenceMultiplier;
      if (!Number.isFinite(canonicalValue) || canonicalValue < 20 || canonicalValue > 300) {
        throw new Error(`${label} bedenindeki ${fieldLabel} çevresi tam çevrede 20-300 cm aralığında olmalıdır.`);
      }
    });

    const lengthFields = [
      ['productLengthCm', 'ürün boyu'],
      ['legLengthCm', 'bacak boyu'],
    ] as const;
    lengthFields.forEach(([field, fieldLabel]) => {
      const value = row?.[field];
      if (!hasValue(value)) return;
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue) || numericValue < 10 || numericValue > 250) {
        throw new Error(`${label} bedenindeki ${fieldLabel} 10-250 cm aralığında olmalıdır.`);
      }
    });

    const hasChest = hasValue(row?.chestCircumferenceCm);
    const hasWaist = hasValue(row?.waistCircumferenceCm);
    const hasHip = hasValue(row?.hipCircumferenceCm);
    if (categoryType === 'upper' && !hasChest) {
      throw new Error(`${label} bedeni için göğüs çevresi gereklidir.`);
    }
    if (categoryType === 'upper' && recommendationScope === 'girth_only_verified' && !hasWaist) {
      throw new Error(`${label} bedeni için doğrulanmış üst giyim önerisinde bel çevresi gereklidir.`);
    }
    if (categoryType === 'lower' && (!hasWaist || !hasHip)) {
      throw new Error(`${label} bedeni için bel ve basen çevresi gereklidir.`);
    }
    if (categoryType === 'full' && (!hasChest || !hasWaist || !hasHip)) {
      throw new Error(`${label} bedeni için göğüs, bel ve basen çevresi gereklidir.`);
    }
  });
};

const assertFormContract = (values: any, measurementMode: MeasurementInputMode): void => {
  const name = String(values?.name || '').trim();
  if (name.length < 2 || name.length > 120) throw new Error('Tablo adı 2 ile 120 karakter arasında olmalıdır.');
  if (String(values?.description || '').length > 500) throw new Error('Kısa açıklama en fazla 500 karakter olabilir.');
  if (String(values?.measurementProtocolVersion || '').length > 40) throw new Error('Protokol sürümü en fazla 40 karakter olabilir.');
  if (String(values?.measurementNotes || '').length > 1000) throw new Error('Ölçüm notları en fazla 1000 karakter olabilir.');

  const recommendationScope = values?.recommendationScope as RecommendationScope;
  if (!['reference_only', 'girth_only_verified'].includes(recommendationScope)) {
    throw new Error('Geçerli bir öneri kapsamı seçin.');
  }

  (['chest', 'waist', 'hip'] as const).forEach(zone => {
    const allowance = values?.stretchAllowances?.[zone];
    const observedPercent = Number(allowance?.observedPercent);
    if (!Number.isFinite(observedPercent) || observedPercent < 0 || observedPercent > 60) {
      throw new Error(`${STRETCH_ZONE_LABELS[zone]} esnemesi %0-%60 aralığında olmalıdır.`);
    }
    if (!['unknown', 'low', 'medium', 'high'].includes(allowance?.recovery)) {
      throw new Error(`${STRETCH_ZONE_LABELS[zone]} için geri toplama seçin.`);
    }
    if (!['unverified', 'supplier_spec', 'brand_tested', 'lab_tested'].includes(allowance?.evidence)) {
      throw new Error(`${STRETCH_ZONE_LABELS[zone]} için esneme kanıtı seçin.`);
    }
  });

  const tolerance = Number(values?.manufacturingToleranceCm);
  if (!Number.isFinite(tolerance) || tolerance < 0 || tolerance > 10) {
    throw new Error('Üretim toleransı 0-10 cm aralığında olmalıdır.');
  }

  (['chest', 'waist', 'hip'] as const).forEach(zone => {
    const range = values?.easeRanges?.[zone];
    const minimum = Number(range?.minCm);
    const maximum = Number(range?.maxCm);
    if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || minimum < -30 || minimum > 60 || maximum < -30 || maximum > 60) {
      throw new Error(`${STRETCH_ZONE_LABELS[zone]} bolluk aralığı -30 ile 60 cm arasında olmalıdır.`);
    }
    if (minimum > maximum) {
      throw new Error(`${STRETCH_ZONE_LABELS[zone]} bolluk aralığında minimum, maksimumdan büyük olamaz.`);
    }
  });

  assertRowsContract(
    values?.rows,
    measurementMode,
    values?.categoryType as SizeChartCategoryType,
    recommendationScope,
  );
  if (values.rows.some((row: any) => hasValue(row?.productLengthCm)) && !values.productLengthType) {
    throw new Error('Ürün boyu girildiğinde ürün boyu ölçüm noktası (POM) seçilmelidir.');
  }
  if (values.rows.some((row: any) => hasValue(row?.legLengthCm)) && !values.legLengthType) {
    throw new Error('Bacak boyu girildiğinde bacak boyu ölçüm noktası (POM) seçilmelidir.');
  }
};

const emptyRows = () => ['S', 'M', 'L'].map(label => ({
  label,
  chestCircumferenceCm: null,
  waistCircumferenceCm: null,
  hipCircumferenceCm: null,
  productLengthCm: null,
  legLengthCm: null,
}));

const displayRows = (chart: SizeChart) => chart.rows.map(row => {
  const divisor = chart.sourceInputMode === 'flat_width' ? 2 : 1;
  return {
    label: row.label,
    chestCircumferenceCm: row.chestCircumferenceCm === null ? null : row.chestCircumferenceCm / divisor,
    waistCircumferenceCm: row.waistCircumferenceCm === null ? null : row.waistCircumferenceCm / divisor,
    hipCircumferenceCm: row.hipCircumferenceCm === null ? null : row.hipCircumferenceCm / divisor,
    productLengthCm: row.productLengthCm,
    legLengthCm: row.legLengthCm,
  };
});

const SizeCharts: React.FC = () => {
  const { user } = useAuth();
  const activeBrandId = getActiveBrandId(user);
  const canManage = Boolean(user && ['admin', 'owner', 'manager'].includes(user.role));
  const [form] = Form.useForm();
  const categoryType = Form.useWatch('categoryType', form) as SizeChartCategoryType | undefined;
  const recommendationScope = Form.useWatch('recommendationScope', form) as RecommendationScope | undefined;
  const [charts, setCharts] = useState<SizeChart[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingChart, setEditingChart] = useState<SizeChart | null>(null);
  const [measurementMode, setMeasurementMode] = useState<MeasurementInputMode>('full_circumference');
  const circumferenceMinimum = measurementMode === 'flat_width' ? 10 : 20;
  const circumferenceMaximum = measurementMode === 'flat_width' ? 150 : 300;

  const mappingSequenceRef = useRef(0);
  const productRequestSequenceRef = useRef(0);
  const chartRequestSequenceRef = useRef(0);
  const [mappingChart, setMappingChart] = useState<SizeChart | null>(null);
  const [products, setProducts] = useState<SizeChartProduct[]>([]);
  const [productTotal, setProductTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentVersion, setAssignmentVersion] = useState<number | null>(null);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [productPage, setProductPage] = useState(1);
  const [productPageSize, setProductPageSize] = useState(20);
  const [productSearchDraft, setProductSearchDraft] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const mappingReady = Boolean(mappingChart) && !assignmentsLoading && assignmentVersion !== null;

  const loadCharts = useCallback(async () => {
    const requestSequence = ++chartRequestSequenceRef.current;
    if (!activeBrandId) {
      setCharts([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const nextCharts = await SizeChartService.list(activeBrandId);
      if (chartRequestSequenceRef.current !== requestSequence) return;
      setCharts(nextCharts);
    } catch (error: any) {
      if (chartRequestSequenceRef.current !== requestSequence) return;
      message.error(error.message || 'Kalıp tabloları yüklenemedi.');
    } finally {
      if (chartRequestSequenceRef.current === requestSequence) setLoading(false);
    }
  }, [activeBrandId]);

  useEffect(() => {
    void loadCharts();
    return () => {
      chartRequestSequenceRef.current += 1;
    };
  }, [loadCharts]);

  useEffect(() => {
    if (!mappingChart || !activeBrandId) {
      setProducts([]);
      setProductTotal(0);
      setProductsLoading(false);
      return;
    }

    const mappingSequence = mappingSequenceRef.current;
    const requestSequence = ++productRequestSequenceRef.current;
    setProductsLoading(true);
    const loadProducts = async () => {
      try {
        const result = await SizeChartService.listProducts(activeBrandId, {
          search: productSearch,
          page: productPage,
          pageSize: productPageSize,
        });
        if (mappingSequenceRef.current !== mappingSequence || productRequestSequenceRef.current !== requestSequence) return;
        setProducts(result.products);
        setProductTotal(result.total);
      } catch (error: any) {
        if (mappingSequenceRef.current !== mappingSequence || productRequestSequenceRef.current !== requestSequence) return;
        message.error(error.message || 'Ürünler yüklenemedi.');
      } finally {
        if (mappingSequenceRef.current === mappingSequence && productRequestSequenceRef.current === requestSequence) {
          setProductsLoading(false);
        }
      }
    };
    void loadProducts();

    return () => {
      if (productRequestSequenceRef.current === requestSequence) productRequestSequenceRef.current += 1;
    };
  }, [activeBrandId, mappingChart, productPage, productPageSize, productSearch]);

  const summary = useMemo(() => ({
    active: charts.filter(chart => chart.isActive).length,
    products: charts.reduce((sum, chart) => sum + chart.assignedProductCount, 0),
    sizes: charts.reduce((sum, chart) => sum + chart.rows.length, 0),
  }), [charts]);

  const openCreate = () => {
    setEditingChart(null);
    setMeasurementMode('full_circumference');
    form.resetFields();
    form.setFieldsValue({
      name: '',
      description: '',
      bodyTemplate: 'unisex',
      categoryType: 'upper',
      measurementProtocol: 'iso_18890',
      measurementProtocolVersion: '2018',
      measurementNotes: '',
      sizeSystem: 'mixed',
      sizeGroup: 'regular',
      fitProfile: 'regular',
      recommendationScope: 'reference_only',
      stretchAllowances: emptyStretchAllowances(),
      manufacturingToleranceCm: 1,
      easeRanges: FIT_EASE_DEFAULTS.regular,
      productLengthType: null,
      legLengthType: null,
      rows: emptyRows(),
      isActive: true,
    });
    setDrawerOpen(true);
  };

  const openEdit = (chart: SizeChart) => {
    setEditingChart(chart);
    setMeasurementMode(chart.sourceInputMode);
    form.resetFields();
    form.setFieldsValue({
      name: chart.name,
      description: chart.description || '',
      bodyTemplate: chart.bodyTemplate,
      categoryType: chart.categoryType,
      measurementProtocol: chart.measurementProtocol,
      measurementProtocolVersion: chart.measurementProtocolVersion || '',
      measurementNotes: chart.measurementNotes || '',
      sizeSystem: chart.sizeSystem,
      sizeGroup: chart.sizeGroup,
      fitProfile: chart.fitProfile,
      recommendationScope: chart.recommendationScope,
      stretchAllowances: chart.stretchAllowances,
      manufacturingToleranceCm: chart.manufacturingToleranceCm,
      easeRanges: chart.easeRanges,
      productLengthType: chart.productLengthType,
      legLengthType: chart.legLengthType,
      rows: displayRows(chart),
      isActive: chart.isActive,
    });
    setDrawerOpen(true);
  };

  const changeMeasurementMode = (nextMode: MeasurementInputMode) => {
    if (nextMode === measurementMode) return;
    const multiplier = measurementMode === 'full_circumference' ? 0.5 : 2;
    const rows = (form.getFieldValue('rows') || []).map((row: any) => ({
      ...row,
      chestCircumferenceCm: row.chestCircumferenceCm == null ? null : row.chestCircumferenceCm * multiplier,
      waistCircumferenceCm: row.waistCircumferenceCm == null ? null : row.waistCircumferenceCm * multiplier,
      hipCircumferenceCm: row.hipCircumferenceCm == null ? null : row.hipCircumferenceCm * multiplier,
    }));
    form.setFieldValue('rows', rows);
    setMeasurementMode(nextMode);
  };

  const changeFitProfile = (profile: SizeChartFitProfile) => {
    form.setFieldValue('easeRanges', FIT_EASE_DEFAULTS[profile]);
  };

  const saveChart = async (values: any) => {
    try {
      if (!activeBrandId) throw new Error('Aktif marka seçimi bulunamadı.');
      assertFormContract(values, measurementMode);
      setSaving(true);
      const numericOrNull = (value: unknown): number | null => hasValue(value) ? Number(value) : null;
      const payload: SizeChartPayload = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        measurementSubject: 'garment',
        measurementState: 'relaxed',
        measurementProtocol: values.measurementProtocol,
        measurementProtocolVersion: values.measurementProtocolVersion?.trim() || null,
        measurementNotes: values.measurementNotes?.trim() || null,
        bodyTemplate: values.bodyTemplate,
        categoryType: values.categoryType,
        measurementInputMode: measurementMode,
        sizeSystem: values.sizeSystem,
        sizeGroup: values.sizeGroup,
        fitProfile: values.fitProfile,
        recommendationScope: values.recommendationScope,
        stretchAllowances: {
          chest: {
            observedPercent: Number(values.stretchAllowances.chest.observedPercent),
            recovery: values.stretchAllowances.chest.recovery,
            evidence: values.stretchAllowances.chest.evidence,
          },
          waist: {
            observedPercent: Number(values.stretchAllowances.waist.observedPercent),
            recovery: values.stretchAllowances.waist.recovery,
            evidence: values.stretchAllowances.waist.evidence,
          },
          hip: {
            observedPercent: Number(values.stretchAllowances.hip.observedPercent),
            recovery: values.stretchAllowances.hip.recovery,
            evidence: values.stretchAllowances.hip.evidence,
          },
        },
        manufacturingToleranceCm: Number(values.manufacturingToleranceCm),
        easeRanges: {
          chest: { minCm: Number(values.easeRanges.chest.minCm), maxCm: Number(values.easeRanges.chest.maxCm) },
          waist: { minCm: Number(values.easeRanges.waist.minCm), maxCm: Number(values.easeRanges.waist.maxCm) },
          hip: { minCm: Number(values.easeRanges.hip.minCm), maxCm: Number(values.easeRanges.hip.maxCm) },
        },
        productLengthType: values.productLengthType || null,
        legLengthType: values.legLengthType || null,
        rows: values.rows.map((row: any, index: number) => ({
          label: row.label.trim(),
          sortOrder: index,
          chestCircumferenceCm: numericOrNull(row.chestCircumferenceCm),
          waistCircumferenceCm: numericOrNull(row.waistCircumferenceCm),
          hipCircumferenceCm: numericOrNull(row.hipCircumferenceCm),
          productLengthCm: numericOrNull(row.productLengthCm),
          legLengthCm: numericOrNull(row.legLengthCm),
        })),
        isActive: values.isActive !== false,
        expectedVersion: editingChart?.version,
      };
      if (editingChart) {
        await SizeChartService.update(activeBrandId, editingChart.id, payload);
        message.success('Kalıp tablosu güncellendi. Yeni sürüm bundan sonraki oturumlarda kullanılacak.');
      } else {
        await SizeChartService.create(activeBrandId, payload);
        message.success('Kalıp tablosu oluşturuldu.');
      }
      setDrawerOpen(false);
      setEditingChart(null);
      form.resetFields();
      await loadCharts();
    } catch (error: any) {
      if (editingChart && isSizeChartConflict(error)) {
        message.warning('Tablo siz düzenlerken değişti. Güncel liste yüklendi; tabloyu yeniden açıp değişikliklerinizi tekrar uygulayın.');
        setDrawerOpen(false);
        setEditingChart(null);
        form.resetFields();
        await loadCharts();
      } else {
        message.error(error.message || 'Kalıp tablosu kaydedilemedi.');
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteChart = async (chart: SizeChart) => {
    if (!activeBrandId) return;
    try {
      await SizeChartService.delete(activeBrandId, chart.id, {
        expectedVersion: chart.version,
        expectedAssignmentVersion: chart.assignmentVersion,
      });
      message.success('Kalıp tablosu silindi; ürün eşleşmeleri kaldırıldı.');
      await loadCharts();
    } catch (error: any) {
      if (isSizeChartConflict(error)) {
        message.warning('Tablo veya ürün eşleştirmeleri siz işlem yaparken değişti. Güncel liste yeniden yüklendi.');
        await loadCharts();
      } else {
        message.error(error.message || 'Kalıp tablosu silinemedi.');
      }
    }
  };

  const closeProductMapping = (force = false) => {
    if (mappingSaving && !force) return;
    mappingSequenceRef.current += 1;
    productRequestSequenceRef.current += 1;
    setMappingChart(null);
    setSelectedProductIds([]);
    setAssignmentVersion(null);
    setAssignmentsLoading(false);
    setProducts([]);
    setProductTotal(0);
    setProductsLoading(false);
    setMappingSaving(false);
  };

  const openProductMapping = (chart: SizeChart) => {
    const brandId = activeBrandId;
    if (!brandId) {
      message.error('Aktif marka seçimi bulunamadı.');
      return;
    }

    const mappingSequence = ++mappingSequenceRef.current;
    productRequestSequenceRef.current += 1;
    setSelectedProductIds([]);
    setAssignmentVersion(null);
    setAssignmentsLoading(true);
    setProducts([]);
    setProductTotal(0);
    setProductsLoading(true);
    setMappingSaving(false);
    setProductPage(1);
    setProductSearch('');
    setProductSearchDraft('');
    setMappingChart(chart);

    void (async () => {
      try {
        const assignment = await SizeChartService.getAssignedProductIds(brandId, chart.id);
        if (mappingSequenceRef.current !== mappingSequence) return;
        setSelectedProductIds(assignment.productIds);
        setAssignmentVersion(assignment.assignmentVersion);
      } catch (error: any) {
        if (mappingSequenceRef.current !== mappingSequence) return;
        closeProductMapping(true);
        message.error(error.message || 'Ürün eşleşmeleri yüklenemedi.');
      } finally {
        if (mappingSequenceRef.current === mappingSequence) setAssignmentsLoading(false);
      }
    })();
  };

  const saveProductMapping = async () => {
    if (!mappingChart || !activeBrandId || assignmentVersion === null || !mappingReady) return;
    const mappingSequence = mappingSequenceRef.current;
    const chart = mappingChart;
    try {
      setMappingSaving(true);
      const result = await SizeChartService.replaceProductAssignments(activeBrandId, chart.id, {
        productIds: selectedProductIds,
        expectedAssignmentVersion: assignmentVersion,
      });
      if (mappingSequenceRef.current !== mappingSequence) return;
      message.success(`${result.assignedProductCount} ürün “${chart.name}” tablosuyla eşleştirildi.`);
      closeProductMapping(true);
      await loadCharts();
    } catch (error: any) {
      if (mappingSequenceRef.current !== mappingSequence) return;
      if (isSizeChartConflict(error)) {
        message.warning('Ürün eşleştirmeleri siz işlem yaparken değişti. Güncel liste yüklendi; eşleştirme ekranını yeniden açın.');
        closeProductMapping(true);
        await loadCharts();
      } else {
        message.error(error.message || 'Ürün eşleşmeleri kaydedilemedi.');
      }
    } finally {
      if (mappingSequenceRef.current === mappingSequence) setMappingSaving(false);
    }
  };

  const chartColumns: ColumnsType<SizeChart> = [
    {
      title: 'Tablo',
      key: 'chart',
      render: (_, chart) => (
        <div className="size-chart-name-cell">
          <div className="size-chart-name-line"><Text strong>{chart.name}</Text>{!chart.isActive && <Tag>Pasif</Tag>}</div>
          <Text type="secondary">{chart.description || 'Açıklama eklenmedi'}</Text>
        </div>
      ),
    },
    {
      title: 'Kullanım',
      key: 'scope',
      width: 190,
      render: (_, chart) => (
        <Space direction="vertical" size={3}>
          <Space size={4} wrap>
            <Tag color="blue">{CATEGORY_LABELS[chart.categoryType]}</Tag>
            <Tag color={chart.recommendationScope === 'girth_only_verified' ? 'green' : 'gold'}>
              {RECOMMENDATION_SCOPE_LABELS[chart.recommendationScope]}
            </Tag>
          </Space>
          <Text type="secondary">{BODY_LABELS[chart.bodyTemplate]} · {SIZE_GROUP_LABELS[chart.sizeGroup]}</Text>
        </Space>
      ),
    },
    {
      title: 'Kalıp',
      key: 'fit',
      width: 270,
      render: (_, chart) => (
        <Space direction="vertical" size={3}>
          <Text>{FIT_LABELS[chart.fitProfile]} · ±{chart.manufacturingToleranceCm} cm</Text>
          <Text type="secondary" className="size-chart-stretch-summary">
            {(['chest', 'waist', 'hip'] as const)
              .map(zone => `${STRETCH_ZONE_LABELS[zone]} %${chart.stretchAllowances[zone].observedPercent}`)
              .join(' · ')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Bedenler',
      key: 'sizes',
      width: 240,
      render: (_, chart) => (
        <div className="size-chart-tags">
          {chart.rows.slice(0, 6).map(row => <Tag key={row.label}>{row.label}</Tag>)}
          {chart.rows.length > 6 && <Tag>+{chart.rows.length - 6}</Tag>}
        </div>
      ),
    },
    {
      title: 'Ürün',
      dataIndex: 'assignedProductCount',
      width: 90,
      align: 'center',
      render: value => <Text strong>{value}</Text>,
    },
    {
      title: 'Sürüm',
      dataIndex: 'version',
      width: 80,
      align: 'center',
      render: version => <Tag>v{version}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 250,
      align: 'right',
      render: (_, chart) => (
        <Space>
          <Button
            aria-label={`${chart.name} tablosu için ürün eşleştirmelerini aç`}
            icon={<LinkOutlined />}
            onClick={() => openProductMapping(chart)}
          >
            Ürün eşleştir
          </Button>
          {canManage && (
            <Tooltip title="Düzenle">
              <Button
                aria-label={`${chart.name} kalıp tablosunu düzenle`}
                icon={<EditOutlined />}
                onClick={() => openEdit(chart)}
              />
            </Tooltip>
          )}
          {canManage && (
            <Popconfirm
              title="Kalıp tablosunu sil?"
              description={`${chart.assignedProductCount} ürünün bağlantısı kaldırılacak. Geçmiş öneri snapshot'ları korunur.`}
              okText="Sil"
              cancelText="Vazgeç"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteChart(chart)}
            >
              <Button aria-label={`${chart.name} kalıp tablosunu sil`} danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const productColumns: ColumnsType<SizeChartProduct> = [
    {
      title: 'Ürün',
      key: 'product',
      render: (_, product) => (
        <Space>
          <Image
            width={48}
            height={58}
            preview={false}
            src={product.imageUrl || undefined}
            alt={`${product.title} ürün görseli`}
            fallback="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
          />
          <div><Text strong>{product.title}</Text><br /><Text type="secondary">{product.sku || 'SKU yok'}</Text></div>
        </Space>
      ),
    },
    { title: 'Kategori', dataIndex: 'category', width: 170 },
    {
      title: 'Mevcut tablo',
      key: 'currentChart',
      width: 220,
      render: (_, product) => {
        if (!product.sizeChartId) return <Text type="secondary">Eşleşme yok</Text>;
        if (product.sizeChartId === mappingChart?.id) return <Tag color="green">Bu tablo</Tag>;
        return <Tooltip title="Seçerseniz ürün mevcut tablodan bu tabloya taşınır."><Tag color="gold">{product.sizeChartName || 'Başka tablo'}</Tag></Tooltip>;
      },
    },
  ];

  if (!activeBrandId) {
    return <div className="size-charts-loading"><Spin size="large" /><Text type="secondary">Marka oturumu bekleniyor…</Text></div>;
  }

  return (
    <div className="size-charts-page">
      <div className="size-charts-header">
        <div>
          <Text className="size-charts-eyebrow">FASHSIZE / FIT INTELLIGENCE</Text>
          <Title level={2}>Kalıp Tabloları</Title>
          <Paragraph>Ürünün gerçek çevrelerini, tasarım bolluğunu ve kullanılabilir esnemeyi tek bir ölçü sözleşmesinde yönetin.</Paragraph>
        </div>
        {canManage && <Button type="primary" size="large" icon={<PlusOutlined />} onClick={openCreate}>Yeni tablo oluştur</Button>}
      </div>

      <Row gutter={16} className="size-charts-stats">
        <Col xs={24} sm={8}><Card><Statistic title="Aktif tablo" value={summary.active} prefix={<TableOutlined />} /></Card></Col>
        <Col xs={24} sm={8}><Card><Statistic title="Eşleşmiş ürün" value={summary.products} prefix={<LinkOutlined />} /></Card></Col>
        <Col xs={24} sm={8}><Card><Statistic title="Tanımlı beden satırı" value={summary.sizes} prefix={<AppstoreAddOutlined />} /></Card></Col>
      </Row>

      <Alert
        className="size-charts-measurement-note"
        showIcon
        type="info"
        message="Tek ölçü dili: tam çevre santimetre"
        description="Göğüs, bel ve basen veritabanında daima relaxed bitmiş ürün tam çevresi olarak saklanır. Tedarikçi tablonuz ürünü düz zeminde ölçüyorsa ‘Düz / yarım ölçü’ modunu seçin; yalnız çevre alanları ×2 normalize edilir. Ürün ve bacak boyları lineer ölçü olarak değişmez."
      />

      <Card className="size-charts-table-card" bordered={false}>
        <Table
          rowKey="id"
          columns={chartColumns}
          dataSource={charts}
          loading={loading}
          pagination={false}
          scroll={{ x: 1420 }}
          locale={{ emptyText: <Empty description="Henüz kalıp tablosu yok" image={Empty.PRESENTED_IMAGE_SIMPLE}>{canManage && <Button type="primary" onClick={openCreate}>İlk tabloyu oluştur</Button>}</Empty> }}
        />
      </Card>

      <Drawer
        className="size-chart-drawer"
        title={editingChart ? `“${editingChart.name}” tablosunu düzenle` : 'Yeni kalıp tablosu'}
        width="min(1180px, 96vw)"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnHidden={false}
        extra={<Space><Button onClick={() => setDrawerOpen(false)}>Vazgeç</Button><Button type="primary" loading={saving} onClick={() => form.submit()}>Kaydet</Button></Space>}
      >
        <Form form={form} layout="vertical" onFinish={saveChart} requiredMark="optional">
          <div className="size-chart-section">
            <div className="size-chart-section-heading"><Text strong>Tablo kimliği</Text><Text type="secondary">Bu tabloyu ürün ekiplerinin kolayca ayırt edeceği şekilde adlandırın.</Text></div>
            <Row gutter={16}>
              <Col xs={24} md={14}>
                <Form.Item
                  name="name"
                  label="Tablo adı"
                  rules={[
                    { required: true, whitespace: true, message: 'Tablo adı gereklidir.' },
                    {
                      validator: async (_, value) => {
                        const length = String(value || '').trim().length;
                        if (length < 2 || length > 120) throw new Error('Boşluklar temizlendikten sonra 2-120 karakter girin.');
                      },
                    },
                  ]}
                >
                  <Input size="large" maxLength={120} showCount placeholder="Örn. Likralı bluz tablosu" />
                </Form.Item>
              </Col>
              <Col xs={24} md={10}><Form.Item name="isActive" label="Durum" valuePropName="checked"><Switch checkedChildren="Aktif" unCheckedChildren="Pasif" /></Form.Item></Col>
              <Col span={24}><Form.Item name="description" label="Kısa açıklama" rules={[{ max: 500, message: 'En fazla 500 karakter girin.' }]}><Input.TextArea rows={2} maxLength={500} showCount placeholder="Bu kalıbın hangi koleksiyon, kumaş veya ürün ailesi için kullanıldığını yazın." /></Form.Item></Col>
            </Row>
          </div>

          <div className="size-chart-section">
            <div className="size-chart-section-heading"><Text strong>Fit sözleşmesi</Text><Text type="secondary">Bu parametreler kullanıcı çevresi ile ürün çevresi arasındaki matematiği belirler.</Text></div>
            <Row gutter={16}>
              <Col xs={24} md={6}><Form.Item name="categoryType" label="Ürün grubu" rules={[{ required: true }]}><Select size="large" options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }))} /></Form.Item></Col>
              <Col xs={24} md={6}><Form.Item name="bodyTemplate" label="Hedef profil" rules={[{ required: true }]}><Select size="large" options={Object.entries(BODY_LABELS).map(([value, label]) => ({ value, label }))} /></Form.Item></Col>
              <Col xs={24} md={6}><Form.Item name="sizeSystem" label="Beden sistemi" rules={[{ required: true }]}><Select size="large" options={Object.entries(SIZE_SYSTEM_LABELS).map(([value, label]) => ({ value, label }))} /></Form.Item></Col>
              <Col xs={24} md={6}><Form.Item name="sizeGroup" label="Beden grubu" rules={[{ required: true }]}><Select size="large" options={Object.entries(SIZE_GROUP_LABELS).map(([value, label]) => ({ value, label }))} /></Form.Item></Col>
              <Col xs={24} md={8}><Form.Item name="fitProfile" label="Kalıp bolluğu" rules={[{ required: true }]}><Select size="large" onChange={changeFitProfile} options={Object.entries(FIT_LABELS).map(([value, label]) => ({ value, label }))} /></Form.Item></Col>
              <Col xs={24} md={8}>
                <Form.Item name="recommendationScope" label="Müşteri öneri kapsamı" rules={[{ required: true, message: 'Öneri kapsamını seçin.' }]}>
                  <Select size="large" options={Object.entries(RECOMMENDATION_SCOPE_LABELS).map(([value, label]) => ({ value, label }))} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="manufacturingToleranceCm"
                  label="Üretim toleransı (±)"
                  tooltip="Gerçek ürün nominal POM ölçüsünden bu kadar sapabilir; güvenli tarafta muhafazakâr uygulanır."
                  rules={[
                    { required: true, message: 'Üretim toleransı gereklidir.' },
                    { type: 'number', min: 0, max: 10, message: '0-10 cm aralığında bir değer girin.' },
                  ]}
                >
                  <InputNumber size="large" min={0} max={10} step={0.5} precision={1} addonAfter="cm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Alert
              className="size-chart-scope-alert"
              showIcon
              type={recommendationScope === 'girth_only_verified' ? 'success' : 'warning'}
              message={recommendationScope === 'girth_only_verified'
                ? 'Çevre tabanlı öneriyi yalnız marka ekibinizce doğrulanan tablolar için açın.'
                : 'Güvenli varsayılan: bu tablo müşteriye otomatik beden önerisi vermez.'}
              description={recommendationScope === 'girth_only_verified'
                ? 'Bu kapsam yalnız göğüs, bel ve basen çevrelerinin yeterli olduğu, ölçüm protokolü, tolerans ve bölgesel esneme kanıtı doğrulanmış ürünler içindir. Omuz genişliği, ağ yüksekliği, uyluk çevresi veya benzeri kritik ölçülere bağlı yapılandırılmış ürünler yalnız referans olarak kalmalıdır.'
                : 'Tablo ürün ve marka ekipleri için ölçü referansı olarak saklanır. Ceket, korse ve kalıplı pantolon gibi omuz, ağ yüksekliği, uyluk veya başka kritik POM’lar gerektiren yapılandırılmış ürünleri bu kapsamda tutun.'}
            />
            <Collapse ghost items={[{
              key: 'technical-fit-contract',
              label: 'Teknik fit sözleşmesi ve bölgesel bolluk aralıkları',
              children: (
                <Space direction="vertical" size={18} style={{ width: '100%' }}>
                  <Row gutter={16}>
                    <Col xs={24} md={8}><Form.Item name="measurementProtocol" label="Ölçüm protokolü" rules={[{ required: true }]}><Select options={[{ value: 'iso_18890', label: 'ISO 18890 esaslı relaxed ürün POM' }, { value: 'brand_standard', label: 'Markaya özel standart' }]} /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name="measurementProtocolVersion" label="Protokol sürümü" rules={[{ max: 40, message: 'En fazla 40 karakter girin.' }]}><Input maxLength={40} showCount placeholder="Örn. 2018 / SOP-FIT-04" /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name="measurementNotes" label="Ölçüm notları" rules={[{ max: 1000, message: 'En fazla 1000 karakter girin.' }]}><Input.TextArea rows={3} maxLength={1000} showCount placeholder="Kapalı fermuar, gerilimsiz kumaş…" /></Form.Item></Col>
                  </Row>
                  <div className="size-chart-technical-heading">
                    <Text strong>Bölgesel esneme kanıtı</Text>
                    <Text type="secondary">Elastan oranını değil, her çevre bölgesinde gözlenen uzamayı ve geri toplamayı kaydedin.</Text>
                  </div>
                  <Row gutter={16}>
                    {(['chest', 'waist', 'hip'] as const).map(zone => (
                      <Col xs={24} md={8} key={`stretch-${zone}`}>
                        <div className="size-chart-stretch-card">
                          <Text strong>{STRETCH_ZONE_LABELS[zone]}</Text>
                          <Form.Item
                            name={['stretchAllowances', zone, 'observedPercent']}
                            label="Gözlenen esneme"
                            rules={[
                              { required: true, message: 'Esneme oranı gereklidir.' },
                              { type: 'number', min: 0, max: 60, message: '%0-%60 aralığında girin.' },
                            ]}
                          >
                            <InputNumber min={0} max={60} step={1} precision={1} addonAfter="%" style={{ width: '100%' }} />
                          </Form.Item>
                          <Form.Item name={['stretchAllowances', zone, 'recovery']} label="Geri toplama" rules={[{ required: true, message: 'Geri toplama seçin.' }]}>
                            <Select options={Object.entries(RECOVERY_LABELS).map(([value, label]) => ({ value, label }))} />
                          </Form.Item>
                          <Form.Item name={['stretchAllowances', zone, 'evidence']} label="Kanıt kaynağı" rules={[{ required: true, message: 'Kanıt kaynağı seçin.' }]}>
                            <Select options={Object.entries(STRETCH_EVIDENCE_LABELS).map(([value, label]) => ({ value, label }))} />
                          </Form.Item>
                        </div>
                      </Col>
                    ))}
                  </Row>
                  <Row gutter={16}>
                    {(['chest', 'waist', 'hip'] as const).map(zone => (
                      <Col xs={24} md={8} key={zone}>
                        <Text strong>{STRETCH_ZONE_LABELS[zone]} bolluk aralığı</Text>
                        <Row gutter={8}>
                          <Col span={12}>
                            <Form.Item
                              name={['easeRanges', zone, 'minCm']}
                              label="Minimum"
                              dependencies={[['easeRanges', zone, 'maxCm']]}
                              rules={[
                                { required: true, message: 'Minimum gereklidir.' },
                                { type: 'number', min: -30, max: 60, message: '-30 ile 60 cm arasında girin.' },
                                {
                                  validator: async () => {
                                    const range = form.getFieldValue(['easeRanges', zone]);
                                    if (hasValue(range?.minCm) && hasValue(range?.maxCm) && Number(range.minCm) > Number(range.maxCm)) {
                                      throw new Error('Minimum ≤ maksimum olmalıdır.');
                                    }
                                  },
                                },
                              ]}
                            >
                              <InputNumber min={-30} max={60} step={0.5} precision={1} addonAfter="cm" style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name={['easeRanges', zone, 'maxCm']}
                              label="Maksimum"
                              dependencies={[['easeRanges', zone, 'minCm']]}
                              rules={[
                                { required: true, message: 'Maksimum gereklidir.' },
                                { type: 'number', min: -30, max: 60, message: '-30 ile 60 cm arasında girin.' },
                                {
                                  validator: async () => {
                                    const range = form.getFieldValue(['easeRanges', zone]);
                                    if (hasValue(range?.minCm) && hasValue(range?.maxCm) && Number(range.minCm) > Number(range.maxCm)) {
                                      throw new Error('Minimum ≤ maksimum olmalıdır.');
                                    }
                                  },
                                },
                              ]}
                            >
                              <InputNumber min={-30} max={60} step={0.5} precision={1} addonAfter="cm" style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                    ))}
                  </Row>
                  <Text type="secondary">Pozitif aralık üründe bırakılan alanı, negatif aralık kompresyon / negative ease’i gösterir. Fit profili yalnız düzenlenebilir başlangıç değerlerini doldurur.</Text>
                </Space>
              ),
            }]} />
          </div>

          <div className="size-chart-section size-chart-measurements-section">
            <div className="size-chart-section-heading size-chart-measurements-heading">
              <div><Text strong>Beden ölçüleri</Text><Text type="secondary">Listeden beden seçin veya kendi etiketinizi yazın. Ondalık değer girebilirsiniz.</Text></div>
              <Radio.Group value={measurementMode} onChange={event => changeMeasurementMode(event.target.value)} buttonStyle="solid">
                <Radio.Button value="full_circumference">Tam çevre</Radio.Button>
                <Radio.Button value="flat_width">Düz / yarım ölçü</Radio.Button>
              </Radio.Group>
            </div>
            <Alert
              type={measurementMode === 'flat_width' ? 'warning' : 'success'}
              showIcon
              message={measurementMode === 'flat_width'
                ? 'Göğüs, bel ve basen kaydederken otomatik ×2 çevreye çevrilecek.'
                : 'Göğüs, bel ve basen değerlerini relaxed bitmiş ürün tam çevresi olarak girin.'}
            />

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="productLengthType"
                  label="Ürün boyu ölçüm noktası (POM)"
                  tooltip="Ürün boyu giriyorsanız başlangıç ve bitiş ölçüm noktalarını açıkça seçin."
                  dependencies={['rows']}
                  rules={[{
                    validator: async (_, value) => {
                      const hasProductLength = (form.getFieldValue('rows') || []).some((row: any) => hasValue(row?.productLengthCm));
                      if (hasProductLength && !value) throw new Error('Ürün boyu girildiği için POM seçimi gereklidir.');
                    },
                  }]}
                >
                  <Select allowClear placeholder="Ürün boyu kullanılmıyor" options={Object.entries(PRODUCT_LENGTH_LABELS).map(([value, label]) => ({ value, label }))} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="legLengthType"
                  label="Bacak boyu ölçüm noktası (POM)"
                  dependencies={['rows']}
                  rules={[{
                    validator: async (_, value) => {
                      const hasLegLength = (form.getFieldValue('rows') || []).some((row: any) => hasValue(row?.legLengthCm));
                      if (hasLegLength && !value) throw new Error('Bacak boyu girildiği için POM seçimi gereklidir.');
                    },
                  }]}
                >
                  <Select allowClear placeholder="Bacak boyu kullanılmıyor" options={Object.entries(LEG_LENGTH_LABELS).map(([value, label]) => ({ value, label }))} />
                </Form.Item>
              </Col>
              <Col span={24}><Text type="secondary">Uzunluklar katalogda saklanır; kullanıcı tarafında aynı ölçüm noktası doğrulanana kadar çevre fit skorunu etkilemez.</Text></Col>
            </Row>

            <div className="size-chart-measurements-scroll">
              <div className="size-chart-grid size-chart-grid--header">
                <span>Beden</span><span>Göğüs (cm)</span><span>Bel (cm)</span><span>Basen (cm)</span><span>Ürün boyu</span><span>Bacak boyu</span><span />
              </div>
              <Form.List
                name="rows"
                rules={[{
                  validator: async (_, rows) => {
                    assertRowsContract(
                      rows,
                      measurementMode,
                      categoryType || 'upper',
                      recommendationScope || 'reference_only',
                    );
                  },
                }]}
              >
                {(fields, { add, remove, move }, { errors }) => (
                  <>
                    {fields.map((field, index) => {
                      const rowLabel = String(form.getFieldValue(['rows', field.name, 'label']) || `${index + 1}. satır`).trim();
                      return (
                        <div className="size-chart-grid size-chart-grid--row" key={field.key}>
                          <Form.Item
                            name={[field.name, 'label']}
                            rules={[
                              { required: true, whitespace: true, message: 'Beden etiketi gereklidir.' },
                              {
                                validator: async (_, value) => {
                                  const length = String(value || '').trim().length;
                                  if (length < 1 || length > 24) throw new Error('Boşluklar temizlendikten sonra 1-24 karakter girin.');
                                },
                              },
                            ]}
                          >
                            <AutoComplete options={SIZE_OPTIONS} placeholder="S / 36 / Özel" filterOption={(input, option) => String(option?.value).toLocaleLowerCase('tr-TR').includes(input.toLocaleLowerCase('tr-TR'))} />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, 'chestCircumferenceCm']}
                            rules={[
                              { required: categoryType === 'upper' || categoryType === 'full', message: 'Göğüs gerekli.' },
                              { type: 'number', min: circumferenceMinimum, max: circumferenceMaximum, message: `${circumferenceMinimum}-${circumferenceMaximum} cm aralığında girin.` },
                            ]}
                          >
                            <InputNumber min={circumferenceMinimum} max={circumferenceMaximum} step={0.05} precision={2} decimalSeparator="," placeholder="—" />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, 'waistCircumferenceCm']}
                            rules={[
                              { required: categoryType === 'lower' || categoryType === 'full' || (categoryType === 'upper' && recommendationScope === 'girth_only_verified'), message: 'Bel gerekli.' },
                              { type: 'number', min: circumferenceMinimum, max: circumferenceMaximum, message: `${circumferenceMinimum}-${circumferenceMaximum} cm aralığında girin.` },
                            ]}
                          >
                            <InputNumber min={circumferenceMinimum} max={circumferenceMaximum} step={0.05} precision={2} decimalSeparator="," placeholder="—" />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, 'hipCircumferenceCm']}
                            rules={[
                              { required: categoryType === 'lower' || categoryType === 'full', message: 'Basen gerekli.' },
                              { type: 'number', min: circumferenceMinimum, max: circumferenceMaximum, message: `${circumferenceMinimum}-${circumferenceMaximum} cm aralığında girin.` },
                            ]}
                          >
                            <InputNumber min={circumferenceMinimum} max={circumferenceMaximum} step={0.05} precision={2} decimalSeparator="," placeholder="—" />
                          </Form.Item>
                          <Form.Item name={[field.name, 'productLengthCm']} rules={[{ type: 'number', min: 10, max: 250, message: '10-250 cm aralığında girin.' }]}>
                            <InputNumber min={10} max={250} step={0.5} precision={1} decimalSeparator="," placeholder="—" />
                          </Form.Item>
                          <Form.Item name={[field.name, 'legLengthCm']} rules={[{ type: 'number', min: 10, max: 250, message: '10-250 cm aralığında girin.' }]}>
                            <InputNumber min={10} max={250} step={0.5} precision={1} decimalSeparator="," placeholder="—" />
                          </Form.Item>
                          <Space size={2} className="size-chart-row-actions">
                            <Button aria-label={`${rowLabel} beden satırını yukarı taşı`} type="text" size="small" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => move(index, index - 1)} />
                            <Button aria-label={`${rowLabel} beden satırını aşağı taşı`} type="text" size="small" icon={<ArrowDownOutlined />} disabled={index === fields.length - 1} onClick={() => move(index, index + 1)} />
                            <Button aria-label={`${rowLabel} beden satırını sil`} type="text" danger size="small" icon={<DeleteOutlined />} disabled={fields.length <= 2} onClick={() => remove(field.name)} />
                          </Space>
                        </div>
                      );
                    })}
                    <Form.ErrorList errors={errors} />
                    <Button
                      className="size-chart-add-row"
                      icon={<PlusOutlined />}
                      disabled={fields.length >= 80}
                      onClick={() => add({ label: '', chestCircumferenceCm: null, waistCircumferenceCm: null, hipCircumferenceCm: null, productLengthCm: null, legLengthCm: null })}
                    >
                      {fields.length >= 80 ? 'En fazla 80 beden satırı' : 'Beden satırı ekle'}
                    </Button>
                  </>
                )}
              </Form.List>
            </div>
          </div>
        </Form>
      </Drawer>

      <Modal
        className="size-chart-product-modal"
        width="min(1040px, 96vw)"
        title={mappingChart ? `Ürünleri eşleştir · ${mappingChart.name}` : 'Ürünleri eşleştir'}
        open={Boolean(mappingChart)}
        onCancel={() => closeProductMapping()}
        okText={assignmentsLoading ? 'Eşleşmeler yükleniyor…' : `${selectedProductIds.length} ürünü kaydet`}
        cancelText="Vazgeç"
        confirmLoading={mappingSaving}
        okButtonProps={{ disabled: !mappingReady }}
        cancelButtonProps={{ disabled: mappingSaving }}
        maskClosable={!mappingSaving}
        keyboard={!mappingSaving}
        onOk={saveProductMapping}
        destroyOnHidden
      >
        <Alert
          showIcon
          type="info"
          message="Bir ürün yalnızca bir kalıp tablosuna bağlanabilir."
          description="Başka bir tabloya bağlı ürünü seçerseniz bağlantı güvenli biçimde bu tabloya taşınır. Seçimi kaldırılan ürünün marka tablosu bağlantısı kaldırılır; uygun ve doğrulanmış bir tablo olmadan otomatik beden önerisi sunulmaz."
        />
        <div className="size-chart-product-toolbar">
          <Input
            value={productSearchDraft}
            onChange={event => setProductSearchDraft(event.target.value)}
            onPressEnter={() => { setProductPage(1); setProductSearch(productSearchDraft); }}
            prefix={<SearchOutlined />}
            placeholder="Ürün adı, SKU veya kategori ara"
            allowClear
          />
          <Button onClick={() => { setProductPage(1); setProductSearch(productSearchDraft); }}>Ara</Button>
          <Tag color={mappingReady ? 'blue' : 'default'}>{mappingReady ? `${selectedProductIds.length} seçili` : 'Eşleşmeler yükleniyor'}</Tag>
        </div>
        <Table
          rowKey="id"
          loading={productsLoading || assignmentsLoading}
          columns={productColumns}
          dataSource={products}
          scroll={{ x: 760 }}
          rowSelection={{
            selectedRowKeys: selectedProductIds,
            preserveSelectedRowKeys: true,
            getCheckboxProps: () => ({ disabled: !mappingReady || mappingSaving }),
            onSelect: (record, selected) => {
              if (!mappingReady || mappingSaving) return;
              setSelectedProductIds(current => selected
                ? Array.from(new Set([...current, record.id]))
                : current.filter(id => id !== record.id));
            },
            onSelectAll: (selected, _selectedRows, changedRows) => {
              if (!mappingReady || mappingSaving) return;
              setSelectedProductIds(current => {
                const changedIds = changedRows.map(row => row.id);
                return selected
                  ? Array.from(new Set([...current, ...changedIds]))
                  : current.filter(id => !changedIds.includes(id));
              });
            },
          }}
          pagination={{
            current: productPage,
            pageSize: productPageSize,
            total: productTotal,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: total => `Toplam ${total} ürün`,
            onChange: (page, pageSize) => { setProductPage(page); setProductPageSize(pageSize); },
          }}
        />
      </Modal>
    </div>
  );
};

export default SizeCharts;
